import { FC, useState } from "react";
import { ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import { styles } from "@/pages/Home/stylesHome";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useDeletePostMutation, useGetPostByUidQuery } from "@/store/api/postApi";
import { IPost } from "@/store/api/postApiData";
import ReportModal from "@/components/UI/modal/ReportModal/ReportModal";
import DeleteModal from "@/components/UI/modal/DeleteModal/DeleteModal";
import CreatePostModal from "@/components/UI/modal/CreatePostModal/CreatePostModal";
import CreateLongPostModal from "@/components/UI/modal/CreateLongPostModal/CreateLongPostModal";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import { RequireAuth } from "@/components/RequireAuth";
import BasketSvg from "@/assets/svg/BasketSvg";
import EditPostSvg from "@/assets/svg/EditPostSvg";
import ReportPostSvg from "@/assets/svg/ReportPostSvg";

interface PostOptionsMenuProps {
  postUid: string;
  authorUid?: string | null;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const PostOptionsMenu: FC<PostOptionsMenuProps> = ({
  postUid,
  authorUid,
  anchorEl,
  open,
  onClose,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const isOwner = !!authorUid && !!user?.uid && authorUid === user.uid;

  const [deletePost] = useDeletePostMutation();
  const { data: fullPost } = useGetPostByUidQuery(postUid, { skip: !isOwner || !open });

  const [openReportModal, setOpenReportModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editPostModal, setEditPostModal] = useState(false);
  const [editLongPostModal, setEditLongPostModal] = useState(false);
  const [editPost, setEditPost] = useState<(IPost & { open?: boolean }) | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    success: boolean;
    link?: { text: string; href: string };
  } | null>(null);

  const longPost = fullPost?.isLongForm;

  const handleReport = () => {
    setOpenReportModal(true);
    onClose();
  };

  const handleDelete = () => {
    setOpenDeleteModal(true);
    onClose();
  };

  const handleConfirmDelete = async () => {
    try {
      await deletePost(postUid).unwrap();
    } catch (error) {
      console.error("Delete failed", error);
    } finally {
      setOpenDeleteModal(false);
    }
  };

  const handleEdit = () => {
    if (!fullPost) return;
    setEditPost({ ...fullPost, open: true });
    if (fullPost.isLongForm) {
      setEditLongPostModal(true);
    } else {
      setEditPostModal(true);
    }
    onClose();
  };

  return (
    <>
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
          link={toast.link}
        />
      )}

      <ReportModal
        handleClose={() => setOpenReportModal(false)}
        open={openReportModal}
        postUid={postUid}
        variant="post"
      />

      <DeleteModal
        open={openDeleteModal}
        handleClose={() => setOpenDeleteModal(false)}
        onDelete={handleConfirmDelete}
        variant="post"
        title="Are you sure you want to delete the post?"
        description="You won't be able to recover it"
      />

      <Menu
        id="thread-post-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        MenuListProps={{ sx: { paddingTop: 0, paddingBottom: 0 } }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        PaperProps={styles.postSelectDropdownMenu}
      >
        {isOwner
          ? [
              <MenuItem
                key="edit"
                onClick={handleEdit}
                sx={{ padding: "1rem 2.3rem 0.5rem 1.3314rem", gap: "0.25rem" }}
              >
                <ListItemIcon sx={{ minWidth: "0px !important" }}>
                  <EditPostSvg height={16} width={16} />
                </ListItemIcon>
                <ListItemText>
                  <Typography sx={styles.menuOptionText}>
                    {longPost ? "Edit Article" : "Edit"}
                  </Typography>
                </ListItemText>
              </MenuItem>,
              <MenuItem
                key="delete"
                onClick={handleDelete}
                sx={{ padding: "0.5rem 2.3rem 1rem 1.3314rem", gap: "0.25rem" }}
              >
                <ListItemIcon sx={{ minWidth: "0px !important" }}>
                  <BasketSvg style={{ width: "1rem", height: "1rem" }} />
                </ListItemIcon>
                <ListItemText>
                  <Typography color="error" sx={styles.menuOptionText}>
                    {longPost ? "Delete Article" : "Delete post"}
                  </Typography>
                </ListItemText>
              </MenuItem>,
            ]
          : [
              <RequireAuth key="report">
                <MenuItem
                  onClick={handleReport}
                  sx={{ gap: "0.25rem", padding: "1rem 1.3314rem" }}
                >
                  <ListItemIcon sx={{ minWidth: "0px !important" }}>
                    <ReportPostSvg />
                  </ListItemIcon>
                  <ListItemText>
                    <Typography sx={{ ...styles.menuOptionText, color: "#CE212B" }}>
                      Report a post
                    </Typography>
                  </ListItemText>
                </MenuItem>
              </RequireAuth>,
            ]}
      </Menu>

      {editPostModal && (
        <CreatePostModal
          open={editPostModal}
          handleClose={() => {
            setEditPostModal(false);
            setEditPost(null);
          }}
          editPost={editPost}
          postVariant={
            editPost?.topic
              ? editPost.topic.charAt(0).toUpperCase() + editPost.topic.slice(1).toLowerCase()
              : ""
          }
          setToast={setToast}
        />
      )}

      {editLongPostModal && (
        <CreateLongPostModal
          open={editLongPostModal}
          handleClose={() => {
            setEditLongPostModal(false);
            setEditPost(null);
          }}
          editPost={editPost}
          setToast={setToast}
          setIsLongPostModalOpen={setEditLongPostModal}
        />
      )}
    </>
  );
};

export default PostOptionsMenu;
